import { NavigationContainerRefWithCurrent } from '@react-navigation/native'
import { onlineManager } from '@tanstack/react-query'
import { AppParamList } from 'app/types'
import { HomeStackParamList, UpdateAppScreenParams } from 'home/stack/types'
import React, { createContext, useCallback, useContext, useEffect } from 'react'
import { Linking } from 'react-native'
import { useFiltersContext } from 'schedule/filters/context'
import { useScheduleContext } from 'schedule/schedule_context'

export interface RoutingContextValue {
  handleNavigation<RouteName extends keyof HomeStackParamList>({
    routeName,
    routeParams,
    resetNavigationState,
  }: HandleNavigationProp<RouteName>): void
}

export const RoutingContext = createContext({} as RoutingContextValue)

interface Props {
  children: React.ReactNode
  navigationRef: NavigationContainerRefWithCurrent<AppParamList>
}

export type HandleNavigationProp<RouteName extends keyof HomeStackParamList> =
  undefined extends HomeStackParamList[RouteName]
    ? {
        routeName: RouteName
        routeParams?: undefined | { [key: string]: any }
        resetNavigationState?: boolean
      }
    : {
        routeName: RouteName
        routeParams: HomeStackParamList[RouteName] & { [key: string]: any }
        resetNavigationState?: boolean
      }

export function RoutingProvider(props: Props) {
  const { setSchedule } = useScheduleContext()
  const { setFiltersWithToggle } = useFiltersContext()
  const navigationRef = props.navigationRef

  const handleNavigation = useCallback(
    <RouteName extends keyof HomeStackParamList>({
      routeName,
      routeParams,
      resetNavigationState = false,
    }: HandleNavigationProp<RouteName>) => {
      if (routeName) {
        if (routeName === 'update_app') {
          const url = (routeParams as UpdateAppScreenParams).storeUrl
          if (url) {
            Linking.openURL(url).catch(e => null)
          }
        } else if (routeName === 'open_shifts') {
          if (routeParams && 'schedule' in routeParams) {
            setSchedule(routeParams.schedule)
          } else {
            setSchedule({ calendar: 'helper', openShiftsOnly: true })
          }
          if (routeParams && 'filters' in routeParams) {
            setFiltersWithToggle(routeParams.filters)
          }
          if (resetNavigationState) {
            navigationRef.current?.reset({
              index: 1,
              routes: [
                { name: 'home' },
                { name: 'schedule', params: { date: routeParams?.date } },
              ],
            })
          } else {
            navigationRef.current?.navigate(
              'schedule' as any,
              {
                date: routeParams?.date,
              },
              { pop: true }
            )
          }
        } else if (routeName === 'schedule') {
          if (routeParams?.schedule) {
            setSchedule(routeParams.schedule)
          }
          const scheduleParams = {
            rateCrew: routeParams?.rateCrew,
            rateShifts: routeParams?.rateShifts,
            rateStore: routeParams?.rateStore,
            uri: routeParams?.uri,
            date: routeParams?.date,
            shiftId: routeParams?.shiftId,
          }
          if (resetNavigationState) {
            navigationRef.current?.reset({
              index: 1,
              routes: [
                { name: 'home' },
                { name: 'schedule', params: scheduleParams },
              ],
            })
          } else {
            navigationRef.current?.navigate(
              'schedule' as any,
              scheduleParams as any,
              { pop: true }
            )
          }
        } else {
          if (resetNavigationState) {
            navigationRef.current?.reset({
              index: 1,
              routes: [
                { name: 'home' },
                { name: routeName, params: routeParams },
              ],
            })
          } else {
            navigationRef.current?.navigate(routeName as any, routeParams, {
              pop: true,
            })
          }
        }
      }
    },
    [navigationRef, setFiltersWithToggle, setSchedule]
  )

  useEffect(() => {
    if (!onlineManager.isOnline()) {
      handleNavigation({ routeName: 'home' })
    }
  }, [handleNavigation])

  return (
    <RoutingContext.Provider
      value={{
        handleNavigation,
      }}
    >
      {props.children}
    </RoutingContext.Provider>
  )
}

export function useRoutingContext() {
  return useContext<RoutingContextValue>(RoutingContext)
}
